import { useTranslation } from 'react-i18next';
import { Alert, Box, IconButton, Stack, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';

import { formatMoney } from '../money';
import {
  canAddOptionalItemUnit,
  optionalItemsByDirection,
  optionalItemsSectionVisible,
  optionalItemUnits,
} from '../offerOptionalItems';
import type {
  OfferOptionalItem,
  OptionalItemsHoldError,
  OptionalItemSelection,
} from '../offerOptionalItems';

interface Props {
  /**
   * Los adicionales YA normalizados (`offerOptionalItems`). Se reciben
   * resueltos, y no la oferta cruda, porque quien pone el título afuera decide
   * con la MISMA lista si la sección existe: dos derivaciones parecidas dejan
   * un encabezado solo sobre un bloque vacío.
   */
  items: readonly OfferOptionalItem[];
  /**
   * La pantalla es la del asesor. Los importes son del proveedor y no llevan
   * el markup de la agencia: la misma regla que esconde el congelado del
   * proveedor a la audiencia minorista.
   */
  isAgent: boolean;
  /** Unidades elegidas por código; lo que de acá salga viaja en el hold. */
  selection: OptionalItemSelection;
  onChangeUnits: (detailBookingCode: string, delta: 1 | -1) => void;
  /**
   * El rechazo del hub que pertenece a esta sección
   * (`UNKNOWN_OFFER_OPTIONS` sobre códigos de adicionales, u
   * `OPTIONAL_ITEM_LIMIT`), ya resuelto contra el payload enviado.
   */
  holdError?: OptionalItemsHoldError | null;
  /** Luggage quotes publish a new catalogue; original codes stay selectable. */
  hidePrices?: boolean;
}

/**
 * Lo que el proveedor ofrece ADEMÁS del servicio, en el momento de Revisar —
 * y la elección de cuántas unidades reservar EN el mismo hold
 * (`options.optionalItemCodes`, #2383).
 *
 * Acá no se suma plata: cada fila muestra el precio UNITARIO que publicó la
 * oferta, y el total con adicionales lo devuelve el hold — el modal post-hold
 * pinta el booking autoritativo (21.23). Los topes que deshabilitan el "+"
 * son los mismos que garantiza el mutador de la selección (21.9): el
 * `maxAllowed` del item y el tope global del hold.
 *
 * No confundir con los extras del proveedor (`BookingExtras`): esos se compran
 * sobre la reserva YA creada y viven en Reservado y Confirmado.
 */
export function OfferOptionalItems({
  items,
  isAgent,
  selection,
  onChangeUnits,
  holdError,
  hidePrices = false,
}: Props) {
  const { t } = useTranslation();

  // La MISMA expresión con la que el flujo decide poner el título: el
  // encabezado vive afuera (es el control que pliega en mobile) y no puede
  // quedar solo sobre un bloque vacío.
  if (!optionalItemsSectionVisible({ items, isAgent })) return null;

  // El motivo del hub viaja tal cual (dice qué corregir), detrás del rótulo
  // traducido — mismo criterio que la tarjeta terminal del hold.
  const holdErrorText = holdError
    ? holdError.kind === 'unknown'
      ? t('travelServices.booking.optionalItems.unknownRejected', {
        names: holdError.names.join(', '),
      })
      : [
        t('travelServices.booking.optionalItems.limitRejected'),
        holdError.message,
      ]
        .filter(Boolean)
        .join(' — ')
    : null;

  return (
    <Stack spacing={1}>
      {optionalItemsByDirection(items).map(([direction, group]) => (
        <Box key={direction ?? 'single'}>
          {/* El tramo se rotula solo cuando la oferta lo declara (ida y
              vuelta): cada tramo publica su propio catálogo con sus propios
              códigos, y elegir para la ida no elige para la vuelta. */}
          {direction && (
            <Typography variant="caption" color="text.secondary">
              {t(
                direction === 'return'
                  ? 'travelServices.booking.return'
                  : 'travelServices.booking.outbound',
              )}
            </Typography>
          )}
          <Stack spacing={0.5} mt={direction ? 0.5 : 0}>
            {group.map((item) => {
              const units = optionalItemUnits(selection, item.detailBookingCode);
              return (
                <Box
                  key={item.detailBookingCode}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  px={1.5}
                  py={1}
                  borderRadius={1}
                  bgcolor={units > 0 ? 'action.selected' : 'grey.100'}
                >
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2">{item.name}</Typography>
                    {/* El tope lo publica el operador; 0 es real (no acepta
                        unidades) y deja la fila como información. */}
                    {item.maxAllowed !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        {t('travelServices.booking.optionalItems.maxAllowed', {
                          max: item.maxAllowed,
                        })}
                      </Typography>
                    )}
                  </Box>
                  {/* El importe no se comprime: en 375 px un nombre largo
                      empuja, y la unidad ("por pasajero") tiene que seguir
                      leyéndose entera. */}
                  {!hidePrices && <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="right"
                    sx={{ flexShrink: 0 }}
                  >
                    {formatMoney(item.price)}
                    {/* La unidad solo se rotula si la oferta la afirma: sin el
                        dato, decir "por reserva" convertiría en total un
                        importe que quizá se multiplica. */}
                    {item.perPerson !== undefined &&
                      ` · ${t(
                        item.perPerson
                          ? 'travelServices.booking.optionalItems.perPerson'
                          : 'travelServices.booking.optionalItems.perBooking',
                      )}`}
                  </Typography>}
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <IconButton
                      size="small"
                      disabled={units === 0}
                      onClick={() => onChangeUnits(item.detailBookingCode, -1)}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" minWidth={16} textAlign="center">
                      {units}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={!canAddOptionalItemUnit({ selection, item })}
                      onClick={() => onChangeUnits(item.detailBookingCode, 1)}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
      {/* El rechazo del hub vive EN la sección que lo produjo, no en un toast
          que se va solo: el asesor corrige la selección acá mismo. */}
      {holdErrorText && <Alert severity="error">{holdErrorText}</Alert>}
    </Stack>
  );
}
